import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { IdsParamSchema } from '../../common/ids-param/ids-param';
import { ViewerContextSchema } from '../../visibility/viewer-context/viewer-context';

import { VisibleAnswerSchema } from '../visible-answers';

/**
 * One of a member's photos, as **another member** is allowed to receive it.
 *
 * **There is no url here, and there never will be** (N12). A read is a separate,
 * authorized, short-lived mint (`GET /media/:id/url` → `MediaUrl`), and the
 * decision about whether this viewer may have those bytes at all is made *there*
 * — not by whatever this payload happens to carry. What travels is an id and
 * enough to draw a placeholder.
 *
 * `blurHash`, `width` and `height` are nullable for the reason `MediaAssetSchema`
 * gives: they are null until finalize has decoded the object. A descriptor with
 * no placeholder is still handed over, because a client that dropped the photo
 * would show a member with fewer pictures than they have.
 */
export const MemberPhotoSchema = z
  .object({
    mediaId: UuidSchema,
    /** The placeholder rendered while (or instead of) the real bytes load. */
    blurHash: z.string().min(1).nullable(),
    width: z.number().int().positive().nullable(),
    height: z.number().int().positive().nullable(),
    /** The owner's own arrangement of their grid. */
    sortOrder: z.number().int().nonnegative(),
  })
  .strict();

export type MemberPhoto = z.infer<typeof MemberPhotoSchema>;

/**
 * Everything one member may see of another — the payload `GET /members/:id`
 * answers with, and the only shape a member-to-member profile ever takes.
 *
 * ## The three facts it is built from, and who decides each
 *
 * - **`answers`** come from `ProfileVisibilityResolver` and nowhere else (N18).
 *   Hidden and unanswered questions are both *absent*, indistinguishably.
 * - **`viewerContext`** is computed server-side from whether an **active** match
 *   exists (`MATCHING_IS_MATCH_ACTIVE`), never sent by the client — a client
 *   that could name its own context could name `matched`.
 * - **`photosBlurred`** reflects the owner's `photoVisibility` against that same
 *   context.
 *
 * ## `photosBlurred` is a HINT, not the control
 *
 * It exists so a client does not spend a round trip per photo learning it may
 * not have them. The control is at the mint, which refuses regardless of what
 * this payload said and regardless of whether the client read it. Two
 * independent authorities is the whole point: narrowing what somebody is shown
 * has never been an access control (P15b), so the payload narrows and the mint
 * controls.
 *
 * ## `about` is matched-only, and the schema enforces it
 *
 * `member_profiles.about` is the one free-text field that is **not** a question:
 * no label, no type, no per-question policy — so the resolver never sees it and
 * cannot withhold it. The contract has said since P6 that it is shown to a
 * matched viewer and never before, and the `superRefine` below is what makes
 * that a fact rather than a comment. The composer nulls it in the public
 * context; this refuses the payload outright if that is ever skipped.
 */
export const MemberProfileViewSchema = z
  .object({
    /** The member being looked at. */
    userId: UuidSchema,
    viewerContext: ViewerContextSchema,
    /** The member's own paragraph — `null` in the `public` context, always. */
    about: z.string().nullable(),
    /** In the owner's own order; empty when they have none. */
    photos: z.array(MemberPhotoSchema),
    /** Whether the real bytes are withheld until these two match. */
    photosBlurred: z.boolean(),
    answers: z.array(VisibleAnswerSchema),
  })
  .strict()
  .superRefine((view, ctx) => {
    if (view.viewerContext === 'public' && view.about !== null) {
      ctx.addIssue({
        code: 'custom',
        path: ['about'],
        message: 'A member’s own paragraph is shown only to a matched viewer.',
      });
    }
  });

export type MemberProfileView = z.infer<typeof MemberProfileViewSchema>;

/**
 * How many members one batch request may draw.
 *
 * Deliberately far below `MAX_PAGE_SIZE` (100), which is what `IdsParamSchema`
 * caps at for the admin kit's label resolution. Those two ceilings guard
 * different costs: resolving a hundred *labels* is one query, while drawing a
 * hundred *members* is three per member through the visibility resolver — the
 * resolver's own note calls "a page of twenty browse cards" the bounded case it
 * is designed for, and this stays inside that.
 *
 * A likes segment pages at `DEFAULT_PAGE_SIZE` (20), so one screenful fits in
 * one request with room to spare, which is the number this is chosen against.
 */
export const MEMBER_VIEW_BATCH_MAX = 25;

/**
 * The batch route's query — `?ids=a,b,c`.
 *
 * Nominations and likes are **id-only lists by design** (`NominationSchema`,
 * `LikeSummarySchema`): no member-to-member answer data may travel through the
 * matching module, so the client draws every card from here. Fetching them one
 * at a time would be P14b's N+1 moved up a layer rather than fixed.
 *
 * **Deduplicated before it is counted.** The cost of a request is its *unique*
 * members, and the two lists a client zips together — today's nominations and
 * who liked me — legitimately overlap. Refusing a caller for asking about the
 * same member twice would be a limit on their bookkeeping rather than on our
 * work.
 *
 * **Refused, never clamped**, past the ceiling: a truncated batch returns fewer
 * members than the caller asked about, which is indistinguishable from those
 * members being ineligible — and that is a silence the client would render as
 * "these people are gone".
 */
/**
 * Which population the batch is drawn from.
 *
 * - `visible` — the default and the browse case: everybody the viewer may see,
 *   with a member either of them has blocked **omitted**.
 * - `blocked` — the viewer's own blocked list, and nothing else. The named ids
 *   are **intersected** with the set of members the viewer is in a block
 *   relation with, rather than the block exclusion simply being skipped, so the
 *   scope cannot be used to look at anybody the viewer has not blocked. Every
 *   other rung of the ladder — deleted, not approved, hiding, same gender — still
 *   applies, so a blocked member who has since closed their account is omitted
 *   here too and the row draws as unavailable.
 *
 * It is a scope and not a filter: a member is entitled to see who **they**
 * blocked (they chose it, and they need to recognise the row to undo it), and
 * that entitlement is the one thing the block exclusion is not protecting them
 * from. Making it an explicit named value rather than an implicit carve-out
 * inside the default path is what keeps the browse case unable to reach it.
 */
export const MEMBER_VIEW_SCOPES = ['visible', 'blocked'] as const;

export const MemberViewScopeSchema = z.enum(MEMBER_VIEW_SCOPES);

export type MemberViewScope = (typeof MEMBER_VIEW_SCOPES)[number];

export const MemberViewListQuerySchema = z
  .object({
    ids: IdsParamSchema.transform((ids) => [...new Set(ids)]).refine(
      (ids) => ids.length <= MEMBER_VIEW_BATCH_MAX,
      { message: `A batch may name at most ${MEMBER_VIEW_BATCH_MAX} members.` },
    ),
    scope: MemberViewScopeSchema.default('visible'),
  })
  .strict();

export type MemberViewListQuery = z.infer<typeof MemberViewListQuerySchema>;
